import React, { useContext, useEffect, useState, useId } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { useSearchParams, useLocation } from 'react-router-dom';

import { Typography } from 'design-system/src/components/Typography/Typography';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import TabList from 'design-system/src/components/TabList';

import { AuthContext } from '../auth';
import { client } from '../api';
import PageContainer from '../components/PageContainer';
import NotificationSettings from '../components/settings/NotificationSettings';
import UsersTable from '../components/settings/UsersTable';
import Profile from '../components/settings/Profile';
import { Table, TableCell, TableHeader } from '../components/Table';
import { PROVIDER_SUPPORT_CONTACT_INFO } from '../constants';

type Tab = 'notifications' | 'users' | 'profile' | 'config';

type ConfigData = {
  show_config: boolean;
  icd_mapping: Record<string, string>;
};

const SettingsTabs = styled(TabList)`
  margin: 20px 0;
`;

const HelperText = styled(Typography)`
  padding-left: 0.5rem;
`;

// TODO(design-system): Replace with Link component
const Link = styled.a`
  color: var(--dark-gray);
  font-weight: 600;
  text-decoration: none;
  border-bottom: 1px solid var(--medium-gray);
  padding-bottom: 2px;
`;

const ConfigContainer = styled.div`
  max-width: 40em;
`;

const StyledTabDescription = styled.div`
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border-gray);
  margin-bottom: 20px;
`;

const ConfigView: React.FC<{ data: ConfigData }> = ({ data }) => {
  const { icd_mapping } = data;
  return (
    <ConfigContainer>
      <HelperText styledAs="bodyDMSans" marginBottom="1rem">
        Below are your account settings for default ICD-10 codes to use on prior authorization requests for specific
        medications, unless otherwise specified in clinical documentation.
        <br />
        <br />
        You can <Link href={`mailto:${PROVIDER_SUPPORT_CONTACT_INFO.email}`}>contact support</Link> at any time to
        update these settings.
      </HelperText>
      <Table>
        <thead>
          <tr>
            <TableHeader>Drug Name</TableHeader>
            <TableHeader>ICD-10 Code</TableHeader>
          </tr>
        </thead>
        <tbody>
          {Object.entries(icd_mapping).map(([ingredient, code]) => (
            <tr key={ingredient}>
              <TableCell>{ingredient}</TableCell>
              <TableCell>{code}</TableCell>
            </tr>
          ))}
        </tbody>
      </Table>
    </ConfigContainer>
  );
};

const TabDescription: React.FC<{ tab: Tab }> = ({ tab }) => {
  if (tab !== 'notifications') {
    return null;
  }

  return (
    <StyledTabDescription>
      <Typography styledAs="bodySmallDMSans" weight={600}>
        Customize your portal experience
      </Typography>
      <Typography styledAs="bodySmallDMSans">
        Set which providers' prescriptions are shown by default in the portal and which ones you receive email alerts
        for.
      </Typography>
    </StyledTabDescription>
  );
};

const SettingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabInState, setTabInState] = useState<Tab>((searchParams.get('tab') as Tab) || 'notifications');
  const authContext = useContext(AuthContext);
  const location = useLocation();
  const tabListId = useId();
  const tabPanelId = useId();

  // if the user switches tabs using the link in the NavSidebar after the component has mounted,
  // we need to update the tab in state to match the new tab in the url
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab) {
      setTabInState(tab as Tab);
    }
  }, [location]);

  const setTab = React.useCallback(
    (tab: Tab) => {
      setTabInState(tab);
      setSearchParams({ tab });
    },
    [setSearchParams],
  );

  const configQuery = useQuery<ConfigData>({
    queryKey: ['config'],
    queryFn: () => {
      return client.get(`config`).then((res) => res.data);
    },
  });

  let body;
  let memberId: string | undefined;

  if (configQuery.isLoading) {
    body = <LoadingSpinner />;
  } else if (configQuery.error) {
    body = <PageLoadError />;
  } else if (!configQuery.data) {
    body = null;
  } else {
    const showConfig = configQuery.data.show_config;
    const isAdmin = authContext.user?.is_admin;
    memberId = authContext.user?.member_id;

    const tabs = [
      { value: 'notifications', label: 'Notifications & Filters' },
      ...(isAdmin ? [{ value: 'users', label: 'Users' }] : []),
      { value: 'profile', label: 'Profile' },
      ...(showConfig ? [{ value: 'config', label: 'Other Configurations' }] : []),
    ];

    body = (
      <>
        <SettingsTabs
          tabs={tabs}
          selectedValue={tabInState}
          onSelect={(value) => setTab(value as Tab)}
          tabListId={tabListId}
          tabPanelId={tabPanelId}
          ariaLabel="Settings navigation"
          variant="fullWidthWithBorder"
        />

        <div role="tabpanel" id={tabPanelId} aria-labelledby={tabListId}>
          <TabDescription tab={tabInState} />

          {tabInState === 'notifications' && memberId && <NotificationSettings memberId={memberId} autoSave />}
          {tabInState === 'users' && isAdmin && <UsersTable />}
          {tabInState === 'profile' && <Profile />}
          {tabInState === 'config' && showConfig && <ConfigView data={configQuery.data} />}
        </div>
      </>
    );
  }

  return (
    <PageContainer title="Settings" width="fitContent">
      {body}
    </PageContainer>
  );
};

export default SettingsPage;
